import { DeleteOutlined } from '@ant-design/icons';
import { usePostImmagini } from '@framework/api-client/api/procedures/immagine-controller/immagine-controller.ts';
import { type PostImmaginiParams } from '@framework/api-client/api/procedures/schemas/postImmaginiParams.ts';
import { useInputController } from '@framework/form/hooks/useInputController.ts';
import { UploadInput } from '@framework/form/inputs/upload/UploadInput.tsx';
import { locales } from '@framework/i18n/config.ts';
import { Button, message, Spin } from 'antd';
import styled from 'styled-components';
import { useDocumentImageOwner } from './DocumentImageOwnerContext.tsx';
import { useImagePreviewUrl } from './useImagePreviewUrl.ts';

const StyledPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const StyledPreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
`;

const StyledPreviewPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 80px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background-color: #f5f5f5;
`;

export interface IImageUploadInputProps {
  name: string;
}

/**
 * Input per il caricamento di un'immagine: carica il file tramite
 * l'API immagini e registra l'id dell'immagine nel campo del form.
 * Quando un'immagine è presente mostra l'anteprima con la possibilità
 * di rimuoverla.
 */
export function ImageUploadInput({ name }: IImageUploadInputProps) {
  const {
    field: { value: imageId, onChange: onImageChange },
  } = useInputController(name, {
    enabled: true,
  });

  const documentOwner = useDocumentImageOwner();

  const { url: previewUrl, isLoading: isPreviewLoading } =
    useImagePreviewUrl(imageId);

  const uploadImage = usePostImmagini({
    request: {
      headers: {
        'Accept-Language': locales['it-IT'].tag,
        'Content-Type': 'multipart/form-data',
      },
    },
  });

  if (imageId) {
    return (
      <StyledPreviewContainer>
        {previewUrl ? (
          <StyledPreviewImage src={previewUrl} alt="" />
        ) : (
          <StyledPreviewPlaceholder>
            {isPreviewLoading ? <Spin /> : null}
          </StyledPreviewPlaceholder>
        )}
        <Button
          icon={<DeleteOutlined />}
          onClick={() => onImageChange(null)}
          danger
        >
          Rimuovi immagine
        </Button>
      </StyledPreviewContainer>
    );
  }

  return (
    <UploadInput
      name={name}
      accept="image/*"
      maxCount={1}
      showUploadList={false}
      title="Carica immagine"
      buttonProps={{ loading: uploadImage.isLoading }}
      beforeUpload={file => {
        const params = {
          descrizione: file.name,
          nomeFile: file.name,
          contentType: file.type,
        } as PostImmaginiParams;

        if (documentOwner.idTipoDocumento) {
          params.idTipoDocumento = documentOwner.idTipoDocumento;
        } else if (documentOwner.idLayoutTipoDocumento) {
          params.idLayoutTipoDocumento = documentOwner.idLayoutTipoDocumento;
        }

        uploadImage
          .mutateAsync({
            data: { file },
            params,
          })
          .then(result => {
            onImageChange(result.id ?? null);
          })
          .catch(() => {
            message.error(
              "Si è verificato un problema durante il caricamento dell'immagine. Riprovare.",
            );
          });
        // Impedisce l'upload automatico di antd: il caricamento
        // viene gestito manualmente tramite l'API immagini
        return false;
      }}
    />
  );
}
